import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Plus, Edit2, Trash2, Sparkles, Wrench, Zap, X, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/changelog`;

export default function ChangelogAdmin() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, duration: 15 });
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    const [entriesR, settingsR] = await Promise.all([
      axios.get(`${API}/entries`).catch(() => ({ data: [] })),
      axios.get(`${API}/splash-settings`).catch(() => ({ data: { enabled: true, duration: 15 } })),
    ]);
    setEntries(entriesR.data);
    setSettings(settingsR.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const categoryIcons = { feature: Sparkles, fix: Wrench, improvement: Zap };
  const categoryLabels = { feature: 'New Feature', fix: 'Bug Fix', improvement: 'Improvement' };

  const EntryForm = ({ initial, onSave, onCancel }) => {
    const [form, setForm] = useState(initial || {
      date: new Date().toISOString().split('T')[0],
      title: '', items: [''], category: 'feature',
    });

    const addItem = () => setForm(f => ({ ...f, items: [...f.items, ''] }));
    const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
    const updateItem = (i, val) => setForm(f => ({ ...f, items: f.items.map((item, idx) => idx === i ? val : item) }));

    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Title</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-slate-500">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Type</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
                <option value="feature">Feature</option>
                <option value="fix">Fix</option>
                <option value="improvement">Improvement</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500">Items (one per line)</label>
          {form.items.map((item, i) => (
            <div key={i} className="flex gap-2 mb-1">
              <input value={item} onChange={e => updateItem(i, e.target.value)}
                placeholder={`Change ${i + 1}...`}
                className="flex-1 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
              {form.items.length > 1 && (
                <button onClick={() => removeItem(i)} className="text-slate-500 hover:text-red-600"><X className="w-4 h-4" /></button>
              )}
            </div>
          ))}
          <button onClick={addItem} className="text-xs text-blue-700 hover:text-blue-700 mt-1">+ Add item</button>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-slate-500 hover:text-slate-900 text-sm">Cancel</button>
          <button onClick={() => onSave({ ...form, items: form.items.filter(i => i.trim()) })}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Save</button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="changelog-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Program Updates Log</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage the changelog shown in the login splash screen</p>
        </div>
        <button onClick={() => { setShowAdd(true); setEditing(null); }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Add Entry
        </button>
      </div>

      {/* Splash Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-900 font-semibold">Splash Screen on Login</p>
          <p className="text-xs text-slate-500">Shows recent changes for {settings.duration}s after login (office roles only)</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Duration (s):</label>
            <input type="number" min="5" max="60" value={settings.duration || 15}
              onChange={async e => {
                const dur = parseInt(e.target.value) || 15;
                setSettings(s => ({ ...s, duration: dur }));
                await axios.put(`${API}/splash-settings`, { ...settings, duration: dur });
              }}
              className="w-14 px-2 py-1 bg-white border border-slate-300 rounded text-slate-900 text-sm text-center outline-none" />
          </div>
          <button onClick={async () => {
            const newEnabled = !settings.enabled;
            setSettings(s => ({ ...s, enabled: newEnabled }));
            await axios.put(`${API}/splash-settings`, { ...settings, enabled: newEnabled });
            toast.success(newEnabled ? 'Splash enabled' : 'Splash disabled');
          }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            settings.enabled ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
          }`}>
            {settings.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <EntryForm onCancel={() => setShowAdd(false)} onSave={async (form) => {
          await axios.post(`${API}/entries`, form);
          toast.success('Entry added');
          setShowAdd(false); load();
        }} />
      )}

      {/* Entries list */}
      <div className="space-y-3">
        {entries.map(entry => {
          const Icon = categoryIcons[entry.category] || Sparkles;
          if (editing === entry.id) {
            return (
              <EntryForm key={entry.id} initial={entry}
                onCancel={() => setEditing(null)}
                onSave={async (form) => {
                  await axios.put(`${API}/entries/${entry.id}`, form);
                  toast.success('Updated');
                  setEditing(null); load();
                }} />
            );
          }
          return (
            <div key={entry.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-100">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-blue-700" />
                  <h3 className="text-sm font-semibold text-slate-900">{entry.title}</h3>
                  <span className="text-[10px] text-slate-500 bg-white px-2 py-0.5 rounded">{categoryLabels[entry.category]}</span>
                  <span className="text-[10px] text-slate-500 font-mono">{entry.date}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(entry.id)} className="p-1.5 text-slate-500 hover:text-slate-900"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={async () => {
                    if (!window.confirm('Delete this entry?')) return;
                    await axios.delete(`${API}/entries/${entry.id}`);
                    toast.success('Deleted'); load();
                  }} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <ul className="px-4 py-3 space-y-1">
                {(entry.items || []).map((item, i) => (
                  <li key={i} className="text-xs text-slate-500 flex items-start gap-2">
                    <span className="text-slate-600 mt-0.5">-</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
